import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, RadioGroup, FormControlLabel, Radio, Stack, TextField } from '@mui/material'
import { QuestionFrame, TextFieldQuestion, RadioQuestion, Question } from 'components/questions'
import { useGlobalState } from 'state'
import { blue } from '@mui/material/colors'
import { isNumeric, getQuestionKey } from 'common/utils'


type YearMonth = {
  year: string
  month: string
}

export const Q00 = () => {
  const state = useGlobalState()
  const q = -1

  const answer8 = React.useMemo((): YearMonth => {
    const answer = state.app.getAnswer(q, 8)
    return answer !== null ? answer.value : {year: '', month: ''}
  }, [state.app.answers])

  const answer9 = React.useMemo((): YearMonth => {
    const answer = state.app.getAnswer(q, 9)
    return answer !== null ? answer.value : {year: '', month: ''}
  }, [state.app.answers])

  const isAccept: string = React.useMemo(() => {
    const answer = state.app.getAnswer(q, 99)
    return answer !== null ? answer.value : ''
  }, [state.app.answers])

  const acceptAndNextPage = () => {
    const nextAnswers = {
      ...state.app.answers
    }
    nextAnswers[getQuestionKey(q, 9)] = {value: answer9, valid: true}
    nextAnswers[getQuestionKey(q, 99)] = {value: 'yes', valid: true}
    state.app.setAnswers(nextAnswers)
  }

  const updateYearMonthAnswer = (num: number, field: 'year' | 'month', value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    const answer = state.app.getAnswer(q, num)?.value as YearMonth || {year: '', month: ''}
    answer[field] = numericValue
    state.app.updateAnswer(q, num, answer, answer.year !== '' && answer.month !== '' && isNumeric(answer.year) && isNumeric(answer.month))
  }

  return (
    <QuestionFrame
      q={q}
      centerButton={isAccept !== 'yes' ? <></> : undefined}
      nextPageLabel={'同意して次へ'}
      nextPageAction={acceptAndNextPage}
    >
      <Typography variant='h5' align='center' width={'100%'} fontWeight={'bold'} color={blue[500]} whiteSpace={'pre-wrap'} sx={{textShadow: '1px 1px 1px rgba(0, 0, 0, 0.3)'}} >利用者情報入力</Typography>

      <Typography variant='body2' align='left' width={'100%'} sx={{ my: 4 }}>
        利用にあたっては、一時保存機能により、途中離脱、再回答、ほかのパソコン、ユーザーへの引継ぎなどが可能です。「途中経過を保存」ボタンを押すと暗号キー付きURLが表示されますので、こちらを保管し、次回再開の際、ブラウザにはりつけて使用してください。
      </Typography>

      <TextFieldQuestion num={1} content={'会社名'} />
      <TextFieldQuestion num={2} content={'お名前'} />
      <TextFieldQuestion num={3} content={'連絡先（メール）'} inputType='email' />
      <TextFieldQuestion num={4} content={'電話番号'} width={300} inputType='tel' />
      <TextFieldQuestion num={5} content={'物件名'} />
      <TextFieldQuestion num={6} content={'物件住所'}>
        <Typography variant='body2' color='text.secondary' sx={{ marginTop: 1 }}>
          {'※ 物件住所がマップの初期位置となります。都道府県から市区町村までを入力してください。'}
        </Typography>
      </TextFieldQuestion>
      <TextFieldQuestion num={7} content={'所有者名'} />

      <Question num={8} content={'竣工年月'}>
        <Stack direction='row' spacing={2}>
          <TextField
            value={answer8.year}
            onChange={(e) => updateYearMonthAnswer(8, 'year', e.target.value)}
            InputProps={{ endAdornment: <Typography variant='body1'>年</Typography>, inputProps: { maxLength: 4 }}}
            sx={{ width: 100}}
          />
          <TextField
            value={answer8.month}
            onChange={(e) => updateYearMonthAnswer(8, 'month', e.target.value)}
            InputProps={{ endAdornment: <Typography variant='body1'>月</Typography>, inputProps: { maxLength: 2 }}}
            sx={{ width: 80}}
          />
        </Stack>
      </Question>

      <Question num={9} content={'改修年月'}>
        <Stack direction='row' spacing={2}>
          <TextField
            value={answer9.year}
            onChange={(e) => updateYearMonthAnswer(9, 'year', e.target.value)}
            InputProps={{ endAdornment: <Typography variant='body1'>年</Typography>, inputProps: { maxLength: 4 }}}
            sx={{ width: 100}}
          />
          <TextField
            value={answer9.month}
            onChange={(e) => updateYearMonthAnswer(9, 'month', e.target.value)}
            InputProps={{ endAdornment: <Typography variant='body1'>月</Typography>, inputProps: { maxLength: 2 }}}
            sx={{ width: 80}}
          />
        </Stack>
      </Question>

      <RadioQuestion
        num={10}
        content={'用途'}
        row
        items={state.scoring.getRadioItems('q1-11')}
      />
      <TextFieldQuestion num={11} content={'延床面積'} width={200} unitLabel='㎡' inputType='number' />
      <TextFieldQuestion num={12} content={'地上階数'} width={100} inputType='number' />
      <TextFieldQuestion num={13} content={'地下階数'} width={100} inputType='number' />

      <Typography variant='body1' fontWeight={'bold'}>{'【個人情報の取り扱いについて】'}</Typography>
      <Typography variant='body1'>{'本サービスでは、サービス提供のために氏名・連絡先等の個人情報を取得します。'}</Typography>
      <Typography variant='body1'>{'ご入力いただく個人情報は、当社の「プライバシーポリシー」に基づき、適切に管理・利用いたします。'}</Typography>
      <Typography variant='body1'>{'同意のうえ、ご入力ください。'}</Typography>
    </QuestionFrame>
  )
}
